import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon } from '../../icons';
import { Badge, Btn, Card, SectionHeader, DataTable, FormRow, Field, Tabs, FileUpload, Modal, FormActions, SearchableSelect } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import NoticeService from '../../api/notice';
import AmenityService from '../../api/amenity'; // For units
import { uploadFiles, getFile, downloadFile } from '../../api/files';
import { toast } from 'react-hot-toast';
import { formatDateByPreference } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

const getPenaltyTemplates = (t) => [
  {
    id: 1,
    name: t("tpl1_name"),
    icon: "💰",
    desc: t("tpl1_desc"),
    subject: t("tpl1_subject"),
    content: t("tpl1_content")
  },
  {
    id: 2,
    name: t("tpl2_name"),
    icon: "🚗",
    desc: t("tpl2_desc"),
    subject: t("tpl2_subject"),
    content: t("tpl2_content")
  },
  {
    id: 3,
    name: t("tpl3_name"),
    icon: "🔊",
    desc: t("tpl3_desc"),
    subject: t("tpl3_subject"),
    content: t("tpl3_content")
  },
  {
    id: 4,
    name: t("tpl4_name"),
    icon: "🐕",
    desc: t("tpl4_desc"),
    subject: t("tpl4_subject"),
    content: t("tpl4_content")
  },
  // {
  //   id: 5,
  //   name: t("tpl5_name"),
  //   icon: "🗑️",
  //   desc: t("tpl5_desc"),
  //   subject: t("tpl5_subject"),
  //   content: t("tpl5_content")
  // },
  // {
  //   id: 6,
  //   name: t("tpl6_name"),
  //   icon: "🔨",
  //   desc: t("tpl6_desc"),
  //   subject: t("tpl6_subject"),
  //   content: t("tpl6_content")
  // },
  // {
  //   id: 7,
  //   name: t("tpl7_name"),
  //   icon: "🏊",
  //   desc: t("tpl7_desc"),
  //   subject: t("tpl7_subject"),
  //   content: t("tpl7_content")
  // },
  // {
  //   id: 8,
  //   name: t("tpl8_name"),
  //   icon: "⚠️",
  //   desc: t("tpl8_desc"),
  //   subject: t("tpl8_subject"),
  //   content: t("tpl8_content")
  // }
];

const SPINNER = (
  <span style={{ width: 10, height: 10, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite", marginRight: 4 }} />
);

const AI_BTN_STYLE = {
  background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
  color: "#fff",
  border: "none",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 700,
  padding: "3px 8px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  lineHeight: 1,
};

const ALL_TAB_TYPES = ["notice", "mom", "resolution", "letter", "sop", "penalty"];

export default function NoticesScreen() {
  const { user } = useAuth();
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.notices?.[key] || translations.en?.notices?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };

  const PENALTY_TEMPLATES = getPenaltyTemplates(t);
  const isAdmin = user?.roles_list?.some(r => ['super_admin', 'admin'].includes(r));
  const isMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) && !isAdmin;
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [pubItem, setPubItem] = useState(null);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [aiNotesLoading, setAiNotesLoading] = useState(false);
  const [aiDecisionsLoading, setAiDecisionsLoading] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [viewFiles, setViewFiles] = useState(null);
  const [noticeBlock, setNoticeBlock] = useState("");

  const noticeAvailableBlocks = useMemo(() => {
    const blocks = Array.from(new Set((units || []).map(u => u.block).filter(Boolean)));
    return blocks.sort();
  }, [units]);

  const noticeFilteredUnits = useMemo(() => {
    if (!noticeBlock) {
      if (noticeAvailableBlocks.length === 0) return units || [];
      return [];
    }
    return (units || []).filter(u => String(u.block) === String(noticeBlock));
  }, [units, noticeBlock, noticeAvailableBlocks]);

  const today = new Date().toISOString().split('T')[0];
  const def = { title: "", category: "General", content: "", publish_date: today, expiry_date: "", files: [], unit_no: "", recipient: "", attendees: "", decisions: "", notes: "", type: "Outgoing", status: "Passed", approved_by: "" };
  const [form, setForm] = useState(def);
  const [errors, setErrors] = useState({});
  const f = k => e => {
    const val = e?.target ? e.target.value : e;
    setForm(x => ({ ...x, [k]: val }));
    setErrors(prev => {
      if (!prev || !prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const [items, setItems] = useState({
    Notices: [],
    MoM: [],
    Resolutions: [],
    Letters: [],
    SOPs: [],
    "Penalties & Notice": [],
  });

  const [noticeLimit, setNoticeLimit] = useState(-1);
  const [noticesThisMonth, setNoticesThisMonth] = useState(0);

  // Free plan limit is 5 notices/month, so they don't get premium tabs
  const isFreePlan = noticeLimit !== -1 && noticeLimit <= 5;
  const showPremiumTabs = !isMember && !isFreePlan;

  const TABS = showPremiumTabs ? [t("tab_notices"), t("tab_mom"), t("tab_resolutions"), t("tab_letters"), t("tab_sops"), t("tab_penalties")] : [t("tab_notices"), t("tab_penalties")];
  const tabKeys = showPremiumTabs ? ["Notices", "MoM", "Resolutions", "Letters", "SOPs", "Penalties & Notice"] : ["Notices", "Penalties & Notice"];

  // Need to map the active tab index to the correct ALL_TAB_TYPES value since tabs array is dynamic
  const _tabMap = showPremiumTabs
    ? [0, 1, 2, 3, 4, 5] // matches ALL_TAB_TYPES index
    : [0, 5];            // tab 0 -> notice, tab 1 -> penalty

  const currentTabType = ALL_TAB_TYPES[_tabMap[tab]] || "notice";
  const catColors = { Emergency: "red", General: "blue", Finance: "green", Maintenance: "amber", Event: "purple", Legal: "gray" };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const type = currentTabType;
      const res = await NoticeService.getNotices(type);
      setItems(prev => ({ ...prev, [tabKeys[tab]]: res.data }));

      if (res.meta) {
        setNoticeLimit(res.meta.notice_limit ?? -1);
        setNoticesThisMonth(res.meta.notices_this_month ?? 0);
      }

      if (type === 'penalty' && units.length === 0) {
        const uRes = await AmenityService.getUnits();
        setUnits(uRes.data);
      }
    } catch (err) {
      toast.error(t("toast_load_failed"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tab, units.length]);

  const lastFetchParams = useRef(null);
  useEffect(() => {
    const currentParams = JSON.stringify({ tab });
    if (lastFetchParams.current === currentParams) return;
    lastFetchParams.current = currentParams;
    fetchData();
  }, [fetchData, tab]);

  const saveItem = async (draftStatus) => {
    const newErrors = {};

    if (!form.title || !form.title.trim()) {
      newErrors.title = currentTabType === "letter" ? t("val_subject") : t("val_title");
    }
    if (currentTabType === "letter" && (!form.recipient || !form.recipient.trim())) {
      newErrors.recipient = t("val_recipient");
    }
    if (currentTabType === "penalty" && (!form.unit_no || !form.unit_no.trim())) {
      newErrors.unit_no = t("val_unit");
    }
    if (currentTabType === "mom" && (!form.notes || !form.notes.trim())) {
      newErrors.notes = t("val_notes");
    }
    if (currentTabType !== "letter" && currentTabType !== "mom" && (!form.content || !form.content.trim())) {
      newErrors.content = currentTabType === "sop" ? t("val_procedure") : currentTabType === "penalty" ? t("val_reason") : t("val_content");
    }
    if (form.publish_date && form.expiry_date && form.expiry_date < form.publish_date) {
      newErrors.expiry_date = "Expiry date cannot be earlier than publish date";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstMsg = Object.values(newErrors)[0];
      toast.error(firstMsg);
      return;
    }

    setErrors({});
    const status = typeof draftStatus === 'string' ? draftStatus : 'published';
    setLoading(true);
    try {
      // Handle File Uploads
      let finalFiles = [];
      const currentFiles = form.files || [];
      const newFiles = currentFiles.filter(f => f instanceof File);
      const existingFiles = currentFiles.filter(f => !(f instanceof File));

      if (newFiles.length > 0) {
        const uploadRes = await uploadFiles(newFiles, 'notices');
        // FileController returns { success: true, data: { uploaded: [...], errors: [...] } }
        const newlyUploaded = uploadRes.data?.data?.uploaded || uploadRes.data?.uploaded || [];
        finalFiles = [...existingFiles, ...newlyUploaded];
      } else {
        finalFiles = existingFiles;
      }

      const payload = {
        ...form,
        type: currentTabType,
        file_ids: finalFiles.map(f => f.id).filter(id => id),
        meta: {
          attendees: form.attendees,
          decisions: form.decisions,
          notes: form.notes,
          unit_no: form.unit_no,
          recipient: form.recipient,
          letter_type: form.type,
          status: form.status,
          approved_by: form.approved_by,
          draft_status: status
        }
      };

      if (editItem) {
        await NoticeService.updateNotice(editItem.id, payload);
        toast.success(t("toast_updated"));
      } else {
        await NoticeService.createNotice(payload);
        toast.success(t("toast_created"));
      }
      setModal(null);
      setEditItem(null);
      setForm(def);
      setErrors({});
      fetchData();
    } catch (err) {
      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors;
        const mappedErrors = {};
        Object.keys(backendErrors).forEach(key => {
          const val = Array.isArray(backendErrors[key]) ? backendErrors[key][0] : backendErrors[key];
          const cleanKey = key.replace('meta.', '');
          mappedErrors[cleanKey] = val;
        });
        setErrors(mappedErrors);
        const firstMsg = Object.values(mappedErrors)[0];
        toast.error(firstMsg);
      } else {
        const msg = err.response?.data?.message || err.message || t("toast_save_failed");
        toast.error(msg);
      }
      console.error(err);
    } finally {
      setLoading(false);
      setAiNotesLoading(false);
      setAiDecisionsLoading(false);
      setRewriteLoading(false);
    }
  };
  const saveDraft = () => saveItem("draft");
  const publishItem = () => saveItem("published");

  const delConfirm = async () => {
    setLoading(true);
    try {
      await NoticeService.deleteNotice(delItem.id);
      toast.success(t("toast_deleted"));
      setDelItem(null);
      fetchData();
    } catch (err) {
      toast.error(t("toast_delete_failed"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setLoading(true);
    try {
      await NoticeService.updateNotice(pubItem.id, { meta: { ...pubItem.meta, draft_status: "published" } });
      toast.success(t("toast_published"));
      setPubItem(null);
      fetchData();
    } catch (err) {
      toast.error(t("toast_publish_failed"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelModal = () => {
    setModal(null); setEditItem(null); setForm(def); setErrors({});
    setAiNotesLoading(false); setAiDecisionsLoading(false); setRewriteLoading(false);
  };

  const handleAiNotes = () => {
    setAiNotesLoading(true);
    setTimeout(() => {
      setForm(x => ({ ...x, notes: "1. Review of previous meeting action items\n2. Financial report Q4 FY 2025-26 presented by Treasurer\n3. Maintenance billing update for April\n4. Vendor AMC renewal discussion - TechLift Solutions\n5. Open forum: resident queries" }));
      setAiNotesLoading(false);
    }, 1200);
  };

  const handleAiDecisions = () => {
    setAiDecisionsLoading(true);
    setTimeout(() => {
      setForm(x => ({ ...x, decisions: "1. Approved waterproofing quote of ₹85,000 from WaterShield Co.\n2. Maintenance charges for FY 2026-27 to remain unchanged at ₹3,500/month\n3. AGM scheduled for April 20, 2026 - Nitin Kumar to send invites\n4. TechLift AMC renewal to be processed by April 30" }));
      setAiDecisionsLoading(false);
    }, 1200);
  };

  const handleAiRewrite = () => {
    setRewriteLoading(true);
    setTimeout(() => {
      setForm(x => ({
        ...x,
        content: `RESOLVED: That the undersigned members of the Managing Committee of [Society Name] Housing Society, having duly considered the matter, hereby resolve and approve ${x.content || "the aforementioned proposal"} subject to applicable legal and regulatory compliance. This resolution is passed with the unanimous consent of all members present.`
      }));
      setRewriteLoading(false);
    }, 1400);
  };

  const handleView = async (f) => {
    try {
      const url = f.temporary_url || f.file_url;
      if (url) {
        window.open(url, '_blank');
        return;
      }
      if (!f.id) {
        toast.error(t("toast_file_not_saved"));
        return;
      }
      const res = await getFile(f.id);
      const newUrl = res.data?.data?.temporary_url || res.data?.data?.file_url || res.data?.temporary_url || res.data?.file_url;
      if (newUrl) {
        window.open(newUrl, '_blank');
      } else {
        toast.error(t("toast_url_not_available"));
      }
    } catch (err) {
      toast.error(t("toast_file_open_failed"));
      console.error(err);
    }
  };

  const openWithTemplate = tpl => {
    let subject = tpl.subject;
    let content = tpl.content;
    if (form.unit_no) {
      subject = subject.replace(/\[Unit ?No\]/g, form.unit_no);
      content = content.replace(/\[Unit ?No\]/g, form.unit_no);
    }
    setForm(x => ({ ...x, title: subject, content: content }));
    setEditItem(null);
    setModal(true);
  };

  return (
    <div className="fade-up space-y-4">
      {delItem && <ConfirmModal title={t("title_delete_item")} message={t("msg_delete_item", { title: delItem.title || delItem.subject })} onConfirm={delConfirm} onCancel={() => setDelItem(null)} />}
      {pubItem && <ConfirmModal title={t("title_publish_item")} danger={false} message={t("msg_publish_item", { title: pubItem.title || pubItem.subject })} onConfirm={handlePublish} onCancel={() => setPubItem(null)} confirmText={t("btn_publish")} />}

      {modal && (
        <Modal
          title={{ "notice": t("title_publish_notice"), "mom": t("title_add_mom"), "resolution": t("title_add_resolution"), "letter": t("title_add_letter"), "sop": t("title_add_sop"), "penalty": t("title_issue_penalty") }[currentTabType]}
          onClose={cancelModal}
        >
          {currentTabType === "notice" && <>
            <FormRow><Field label={t("label_title")} value={form.title} onChange={f("title")} required error={errors.title} /></FormRow>
            <FormRow>
              <Field
                label={t("label_category")}
                type="select"
                value={form.category}
                onChange={f("category")}
                options={[
                  { value: "General", label: t("cat_general") },
                  { value: "Emergency", label: t("cat_emergency") },
                  { value: "Maintenance", label: t("cat_maintenance") },
                  { value: "Finance", label: t("cat_finance") },
                  { value: "Event", label: t("cat_event") },
                  { value: "Legal", label: t("cat_legal") }
                ]}
              />
            </FormRow>
            <FormRow cols={2}>
              <Field label={t("label_publish_date")} type="date" value={form.publish_date} onChange={f("publish_date")} error={errors.publish_date} />
              <Field label={t("label_expiry_date")} type="date" value={form.expiry_date} onChange={f("expiry_date")} error={errors.expiry_date} />
            </FormRow>
            <FormRow><Field label={t("label_content")} type="textarea" value={form.content} onChange={f("content")} rows={4} required error={errors.content} /></FormRow>
            <FileUpload label={t("label_attachments")} accept="image/*,.pdf" multiple files={form.files || []} onChange={files => setForm(x => ({ ...x, files }))} />
            <div className="flex gap-2 items-center mt-4 pt-4 border-t border-border">
              <Btn icon="check" variant="primary" onClick={() => saveItem("published")} loading={loading}>{t("btn_publish")}</Btn>
              <Btn icon="save" onClick={() => saveItem("draft")} variant="default" disabled={loading}>{t("btn_save_draft")}</Btn>
              <Btn onClick={cancelModal} variant="default" className="text-muted hover:text-ink">{t("btn_cancel")}</Btn>
            </div>
          </>}

          {currentTabType === "mom" && <>
            <FormRow><Field label={t("label_mom_title")} value={form.title} onChange={f("title")} placeholder={t("ph_mom_title")} required error={errors.title} /></FormRow>
            <FormRow cols={2}><Field label={t("label_meeting_date")} type="date" value={form.publish_date || ""} onChange={f("publish_date")} required error={errors.publish_date} /><Field label={t("label_attendees")} value={form.attendees || ""} onChange={f("attendees")} placeholder={t("ph_attendees")} /></FormRow>
            <div className="mb-3">
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-500">{t("label_notes_agenda")} <span className="text-red-500">*</span></label>
                  <button type="button" style={AI_BTN_STYLE} onClick={handleAiNotes} disabled={aiNotesLoading}>
                    {aiNotesLoading ? SPINNER : "✨"} {aiNotesLoading ? t("btn_generating") : "AI"}
                  </button>
                </div>
                <textarea
                  value={form.notes || ""}
                  onChange={f("notes")}
                  rows={3}
                  placeholder={t("ph_notes")}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none dark:bg-slate-800 ${errors.notes ? "!border-red-500 ring-1 !ring-red-500 focus:!ring-red-200" : "border-border focus:ring-primary/20"}`}
                />
                {errors.notes && <p className="text-xs text-red-500 font-medium mt-1">{errors.notes}</p>}
              </div>
            </div>
            <div className="mb-3">
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-500">{t("label_key_decisions")}</label>
                  <button type="button" style={AI_BTN_STYLE} onClick={handleAiDecisions} disabled={aiDecisionsLoading}>
                    {aiDecisionsLoading ? SPINNER : "✨"} {aiDecisionsLoading ? t("btn_generating") : "AI"}
                  </button>
                </div>
                <textarea
                  value={form.decisions || ""}
                  onChange={f("decisions")}
                  rows={3}
                  placeholder={t("ph_decisions")}
                  className="w-full px-3 py-2 border border-border dark:bg-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                />
              </div>
            </div>
            <FileUpload label={t("label_attachments")} accept="image/*,.pdf" multiple files={form.files || []} onChange={files => setForm(x => ({ ...x, files }))} />
            <div className="flex gap-2 items-center mt-4 pt-4 border-t border-border">
              <Btn icon="check" variant="primary" onClick={() => saveItem("published")}>{t("btn_publish_mom")}</Btn>
              <Btn icon="save" onClick={() => saveItem("draft")} variant="default">{t("btn_save_draft")}</Btn>
              <Btn onClick={cancelModal} variant="default" className="text-muted hover:text-ink">{t("btn_cancel")}</Btn>
            </div>
          </>}

          {currentTabType === "resolution" && <>
            <FormRow><Field label={t("label_resolution_title")} value={form.title} onChange={f("title")} placeholder={t("ph_resolution_title")} required error={errors.title} /></FormRow>
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-500">{t("label_description")} <span className="text-red-500">*</span></label>
                <button type="button" style={AI_BTN_STYLE} onClick={handleAiRewrite} disabled={rewriteLoading}>
                  {rewriteLoading ? SPINNER : "✨"} {rewriteLoading ? t("btn_rewriting") : "Rewrite"}
                </button>
              </div>
              <textarea
                value={form.content}
                onChange={f("content")}
                rows={4}
                placeholder={t("ph_resolution_desc")}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none dark:bg-slate-800 ${errors.content ? "!border-red-500 ring-1 !ring-red-500 focus:!ring-red-200" : "border-border focus:ring-primary/20"}`}
                required
              />
              {errors.content && <p className="text-xs text-red-500 font-medium mt-1">{errors.content}</p>}
            </div>
            <FormRow cols={2}>
              <Field label={t("label_resolution_date")} type="date" value={form.publish_date} onChange={f("publish_date")} required error={errors.publish_date} />
              <Field label={t("label_status")} type="select" value={form.status} onChange={f("status")} options={[{ value: "Passed", label: t("status_passed") }, { value: "Pending", label: t("status_pending") }, { value: "Rejected", label: t("status_rejected") }]} />
            </FormRow>
            <FormRow>
              <Field label={t("label_approved_by")} value={form.approved_by || ""} onChange={f("approved_by")} placeholder={t("ph_approved_by")} />
            </FormRow>
            <FileUpload label={t("label_attachments")} accept="image/*,.pdf" multiple files={form.files || []} onChange={files => setForm(x => ({ ...x, files }))} />
            <div className="flex gap-2 items-center mt-4 pt-4 border-t border-border">
              <Btn icon="check" variant="primary" onClick={() => saveItem("published")} loading={loading}>{t("btn_publish")}</Btn>
              <Btn icon="save" onClick={() => saveItem("draft")} variant="default" disabled={loading}>{t("btn_save_draft")}</Btn>
              <Btn onClick={cancelModal} variant="default" className="text-muted hover:text-ink">{t("btn_cancel")}</Btn>
            </div>
          </>}

          {currentTabType === "letter" && <>
            <FormRow cols={2}>
              <Field label={t("label_type")} type="select" value={form.type || "Outgoing"} onChange={f("type")} options={[{ value: "Outgoing", label: t("type_outgoing") }, { value: "Incoming", label: t("type_incoming") }]} />
              <Field label={t("label_date")} type="date" value={form.publish_date} onChange={f("publish_date")} required error={errors.publish_date} />
            </FormRow>
            <FormRow><Field label={t("label_subject")} value={form.title} onChange={f("title")} placeholder={t("ph_letter_subject")} required error={errors.title} /></FormRow>
            <FormRow><Field label={t("label_recipient_sender")} value={form.recipient || ""} onChange={f("recipient")} placeholder={t("ph_letter_recipient")} required error={errors.recipient} /></FormRow>
            <FormRow><Field label={t("label_content_brief")} type="textarea" value={form.content || ""} onChange={f("content")} rows={4} placeholder={t("ph_letter_content")} error={errors.content} /></FormRow>
            <FileUpload label={t("label_attachments")} accept="image/*,.pdf" multiple files={form.files || []} onChange={files => setForm(x => ({ ...x, files }))} />
            <div className="flex gap-2 items-center mt-4 pt-4 border-t border-border">
              <Btn icon="check" variant="primary" onClick={() => saveItem("published")} loading={loading}>{t("btn_publish")}</Btn>
              <Btn icon="save" onClick={() => saveItem("draft")} variant="default" disabled={loading}>{t("btn_save_draft")}</Btn>
              <Btn onClick={cancelModal} variant="default" className="text-muted hover:text-ink">{t("btn_cancel")}</Btn>
            </div>
          </>}

          {currentTabType === "sop" && <>
            <FormRow><Field label={t("label_sop_title")} value={form.title} onChange={f("title")} required error={errors.title} /></FormRow>
            <FormRow cols={2}>
              <Field label={t("label_category")} type="select" value={form.category} onChange={f("category")} options={[{ value: "General", label: t("cat_general") }, { value: "Security", label: t("cat_security") }, { value: "Housekeeping", label: t("cat_housekeeping") }, { value: "Finance", label: t("cat_finance") }, { value: "Emergency", label: t("cat_emergency") }, { value: "Maintenance", label: t("cat_maintenance") }, { value: "Visitor Management", label: t("cat_visitor") }, { value: "Event", label: t("cat_event") }]} />
              <Field label={t("label_publish_date")} type="date" value={form.publish_date || ""} onChange={f("publish_date")} required error={errors.publish_date} />
            </FormRow>
            <FormRow><Field label={t("label_procedure_steps")} type="textarea" value={form.content} onChange={f("content")} rows={5} placeholder={t("ph_sop_content")} required error={errors.content} /></FormRow>
            <FileUpload label={t("label_attachments")} accept="image/*,.pdf" multiple files={form.files || []} onChange={files => setForm(x => ({ ...x, files }))} />
            <div className="flex gap-2 items-center mt-4 pt-4 border-t border-border">
              <Btn icon="check" variant="primary" onClick={() => saveItem("published")} loading={loading}>{t("btn_publish")}</Btn>
              <Btn icon="save" onClick={() => saveItem("draft")} variant="default" disabled={loading}>{t("btn_save_draft")}</Btn>
              <Btn onClick={cancelModal} variant="default" className="text-muted hover:text-ink">{t("btn_cancel")}</Btn>
            </div>
          </>}

          {currentTabType === "penalty" && <>
            <FormRow cols={2}>
              <SearchableSelect
                label="Block"
                value={noticeBlock}
                placeholder="Select Block"
                searchPlaceholder="Search block..."
                onChange={(e) => {
                  const blk = e.target.value;
                  setNoticeBlock(blk);
                  setForm(prev => ({ ...prev, unit_no: "" }));
                  if (errors.block) setErrors(prev => ({ ...prev, block: null }));
                }}
                options={[
                  { value: "", label: "Select Block" },
                  ...noticeAvailableBlocks.map(b => ({ value: b, label: `Block ${b}` }))
                ]}
                required
                error={errors.block}
              />
              <SearchableSelect
                label={t("label_unit_no")}
                value={form.unit_no || ""}
                placeholder={(noticeBlock || noticeAvailableBlocks.length === 0) ? "Select Unit No." : "Select Block first"}
                searchPlaceholder="Search unit number..."
                disabled={noticeAvailableBlocks.length > 0 && !noticeBlock}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    unit_no: val,
                    title: prev.title.replace(/\[Unit ?No\]/g, val),
                    content: prev.content.replace(/\[Unit ?No\]/g, val)
                  }));
                  if (errors.unit_no) setErrors(prev => ({ ...prev, unit_no: null }));
                }}
                options={[
                  { value: "", label: (noticeBlock || noticeAvailableBlocks.length === 0) ? "Select Unit No." : "Select Block first" },
                  ...noticeFilteredUnits.map(u => ({
                    value: u.unit_number,
                    label: u.unit_number
                  }))
                ]}
                required
                error={errors.unit_no}
              />
            </FormRow>
            <FormRow>
              <Field label={t("label_issue_date")} type="date" value={form.publish_date || today} onChange={f("publish_date")} error={errors.publish_date} />
            </FormRow>
            <FormRow><Field label={t("label_subject")} value={form.title} onChange={f("title")} required error={errors.title} /></FormRow>
            <FormRow><Field label={t("label_notice_content")} type="textarea" value={form.content} onChange={f("content")} rows={5} required error={errors.content} /></FormRow>
            <FileUpload label={t("label_attach_optional")} accept="image/*,.pdf" files={form.files || []} onChange={files => setForm(x => ({ ...x, files }))} />
            <div className="flex gap-2 items-center mt-4 pt-4 border-t border-border">
              <Btn icon="check" variant="primary" onClick={() => saveItem("published")} loading={loading}>{t("btn_publish")}</Btn>
              <Btn icon="save" onClick={() => saveItem("draft")} variant="default" disabled={loading}>{t("btn_save_draft")}</Btn>
              <Btn onClick={cancelModal} variant="default" className="text-muted hover:text-ink">{t("btn_cancel")}</Btn>
            </div>
          </>}

          {currentTabType !== "mom" && currentTabType !== "resolution" && <></>}
        </Modal>
      )}

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {loading && !modal && (
        <Card>
          <div className="animate-pulse flex flex-col gap-3">
            <div className="h-8 bg-slate-100 dark:bg-slate-700 rounded-xl w-48 mb-2"></div>
            {[1, 2, 3].map(i => <div key={i} className="h-24 bg-slate-100 dark:bg-slate-700 rounded-xl w-full"></div>)}
          </div>
        </Card>
      )}

      {!loading && currentTabType === "notice" && (
        <Card>
          <SectionHeader title={t("title_active_notices")} action={!isMember && (
            <div className="hidden lg:block">
              {noticeLimit !== -1 && noticesThisMonth >= noticeLimit ? (
                <Btn sm variant="primary" icon="plus" onClick={() => toast.error(`Monthly limit reached (${noticesThisMonth}/${noticeLimit}). Please upgrade your plan.`)} className="opacity-50 grayscale">{t("btn_new_notice")}</Btn>
              ) : (
                <Btn sm variant="primary" icon="plus" onClick={() => { setEditItem(null); setForm(def); setModal(true); }}>{t("btn_new_notice")}</Btn>
              )}
            </div>
          )} />
          {/* Desktop View */}
          <div className="hidden lg:block space-y-3">
            {items.Notices.map(n => (
              <div key={n.id} className="flex items-center gap-4 p-4 border border-border/80 dark:border-slate-800 rounded-xl hover:border-primary/30 dark:hover:border-slate-700 bg-white/50 dark:bg-slate-900/50 transition-all shadow-xs group">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${n.category === "Emergency" ? "bg-red-500/10 text-red-500" : n.category === "Finance" ? "bg-emerald-500/10 text-emerald-500" : "bg-blue-500/10 text-blue-500"}`}>
                  <Icon name="notices" size={18} />
                </div>
                <div className="flex-1 min-w-0 pr-4">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-sm font-semibold text-ink dark:text-slate-100 truncate">{n.title}</span>
                    <Badge color={catColors[n.category] || "gray"} sm>{t("cat_" + n.category.toLowerCase().replace(" ", "_")) || n.category}</Badge>
                    {n.meta?.draft_status === "draft" && <Badge color="gray" dot sm>{t("status_draft")}</Badge>}
                  </div>
                  <p className="text-xs text-muted dark:text-slate-400 mb-1.5 truncate block">{n.content}</p>
                  <div className="flex items-center gap-4 text-xs text-muted dark:text-slate-400">
                    <span className="flex items-center gap-1">📅 {n.publish_date ? formatDateByPreference(n.publish_date) : '-'}</span>
                    {n.attachments?.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setViewFiles(n.attachments); }}
                        className="flex gap-1.5 items-center hover:bg-primary/10 px-2 py-0.5 rounded-md text-primary dark:text-primary-light font-medium transition-colors"
                      >
                        <Icon name="link" size={11} />
                        {n.attachments.length} {n.attachments.length > 1 ? t("label_attachments_pl") : t("label_attachment")}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 items-center flex-shrink-0">
                  {!isMember && (
                    <>
                      {n.meta?.draft_status === "draft" && <Btn xs icon="check" variant="primary" onClick={() => setPubItem(n)}>{t("btn_publish")}</Btn>}
                      <Btn xs icon="edit" variant="default" onClick={() => { setEditItem(n); setForm({ ...def, ...n, publish_date: n.publish_date ? n.publish_date.split('T')[0] : today, expiry_date: n.expiry_date ? n.expiry_date.split('T')[0] : "", ...n.meta, files: n.attachments || [] }); setModal(true); }}>{t("btn_edit")}</Btn>
                      <Btn xs variant="danger" icon="trash" onClick={() => setDelItem(n)}>{t("btn_del")}</Btn>
                    </>
                  )}
                </div>
              </div>
            ))}
            {items.Notices.length === 0 && <p className="text-sm text-muted text-center py-8">{t("msg_no_active_notices")}</p>}
          </div>

          {/* Mobile View */}
          <div className="block lg:hidden space-y-3 mt-3">
            {items.Notices.length === 0 && <p className="text-sm text-muted text-center py-8">{t("msg_no_active_notices")}</p>}
            {items.Notices.map((n, i) => (
              <IonItemSliding key={n.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                  <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 ${n.category === "Emergency" ? "bg-red-100" : n.category === "Finance" ? "bg-emerald-100" : "bg-blue-100"}`}>
                      <Icon name="notices" size={16} className={n.category === "Emergency" ? "text-red-500" : "text-blue-500"} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-ink truncate mb-1">{n.title}</div>
                      <div className="text-xs text-muted line-clamp-2 leading-snug mb-1">{n.content}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge color={catColors[n.category] || "gray"} sm>{t("cat_" + n.category.toLowerCase().replace(" ", "_")) || n.category}</Badge>
                        {n.meta?.draft_status === "draft" && <Badge color="gray" dot sm>{t("status_draft")}</Badge>}
                        <span className="text-[10px] text-muted">📅 {n.publish_date ? formatDateByPreference(n.publish_date) : '-'}</span>
                      </div>
                    </div>

                    {n.attachments?.length > 0 && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0 items-center justify-center pt-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setViewFiles(n.attachments); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Icon name="link" size={14} />
                        </button>
                      </div>
                    )}

                    {!isMember && (
                      <div className="text-slate-300 ml-1 flex items-center justify-center pt-3">
                        <Icon name="chevron_left" size={14} />
                      </div>
                    )}
                  </div>
                </IonItem>

                {!isMember && (
                  <IonItemOptions side="end">
                    {n.meta?.draft_status === "draft" && (
                      <IonItemOption color="success" onClick={() => setPubItem(n)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="check" size={20} color="white" />
                        </div>
                      </IonItemOption>
                    )}
                    <IonItemOption color="primary" onClick={() => { setEditItem(n); setForm({ ...def, ...n, publish_date: n.publish_date ? n.publish_date.split('T')[0] : today, expiry_date: n.expiry_date ? n.expiry_date.split('T')[0] : "", ...n.meta, files: n.attachments || [] }); setModal(true); }}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="edit" size={20} color="white" />
                      </div>
                    </IonItemOption>
                    <IonItemOption color="danger" onClick={() => setDelItem(n)}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="trash" size={20} color="white" />
                      </div>
                    </IonItemOption>
                  </IonItemOptions>
                )}
              </IonItemSliding>
            ))}
          </div>
        </Card>
      )}

      {!loading && currentTabType === "mom" && (
        <Card>
          <SectionHeader title={t("title_mom")} action={!isMember && <div className="hidden lg:block"><Btn sm variant="primary" icon="plus" onClick={() => { setEditItem(null); setForm(def); setModal(true); }}>{t("btn_add_mom")}</Btn></div>} />
          <div className="hidden lg:block">
            <DataTable cols={[t("col_title"), t("col_meeting_date"), t("col_attendees"), t("col_key_decisions"), t("col_status"), t("col_actions")]}
              rows={items.MoM.map(m => [
                <span className="font-semibold text-sm">{m.title}</span>,
                m.publish_date ? formatDateByPreference(m.publish_date) : '-',
                m.meta?.attendees ? `${m.meta.attendees} members` : "-",
                <div className="max-w-[300px] md:max-w-[400px] lg:max-w-[600px]"><span className="truncate block">{m.meta?.decisions}</span></div>,
                <div className="flex gap-3">
                  <Badge color={m.meta?.draft_status === "draft" ? "gray" : "green"} dot className="w-fit">{m.meta?.draft_status === "draft" ? t("status_draft") : t("status_published")}</Badge>
                </div>,
                <div className="flex gap-2">
                  {m.attachments?.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setViewFiles(m.attachments); }}
                      className="text-sm text-primary dark:text-blue-400 font-medium flex items-center gap-1 hover:underline"
                    >
                      <Icon name="link" size={10} /> {m.attachments.length} {t("label_files")}
                    </button>
                  )}
                  {!isMember && (
                    <>
                      {m.meta?.draft_status === "draft" && <Btn xs icon="check" variant="primary" onClick={() => setPubItem(m)}>{t("btn_publish")}</Btn>}
                      <Btn xs icon="edit" onClick={() => { setEditItem(m); setForm({ ...def, ...m, publish_date: m.publish_date ? m.publish_date.split('T')[0] : today, ...m.meta, files: m.attachments || [] }); setModal(true); }}>{t("btn_edit")}</Btn>
                      <Btn xs variant="danger" icon="trash" onClick={() => setDelItem(m)}>{t("btn_del")}</Btn>
                    </>
                  )}
                </div>
              ])} />
          </div>

          {/* Mobile View */}
          <div className="block lg:hidden space-y-3 mt-3">
            {items.MoM.length === 0 && <p className="text-sm text-muted text-center py-8">No minutes of meeting found.</p>}
            {items.MoM.map((m, i) => (
              <IonItemSliding key={m.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                  <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="users" size={16} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-ink truncate mb-1">{m.title}</div>
                      <div className="text-xs text-muted line-clamp-2 leading-snug mb-1">{m.meta?.decisions || "No key decisions"}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge color={m.meta?.draft_status === "draft" ? "gray" : "green"} dot sm>{m.meta?.draft_status === "draft" ? t("status_draft") : t("status_published")}</Badge>
                        <span className="text-[10px] text-muted">📅 {m.publish_date ? formatDateByPreference(m.publish_date) : '-'}</span>
                        {m.meta?.attendees && <span className="text-[10px] text-muted">👥 {m.meta.attendees}</span>}
                      </div>
                    </div>

                    {m.attachments?.length > 0 && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0 items-center justify-center pt-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setViewFiles(m.attachments); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Icon name="link" size={14} />
                        </button>
                      </div>
                    )}

                    {!isMember && (
                      <div className="text-slate-300 ml-1 flex items-center justify-center pt-3">
                        <Icon name="chevron_left" size={14} />
                      </div>
                    )}
                  </div>
                </IonItem>

                {!isMember && (
                  <IonItemOptions side="end">
                    {m.meta?.draft_status === "draft" && (
                      <IonItemOption color="success" onClick={() => setPubItem(m)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="check" size={20} color="white" />
                        </div>
                      </IonItemOption>
                    )}
                    <IonItemOption color="primary" onClick={() => { setEditItem(m); setForm({ ...def, ...m, publish_date: m.publish_date ? m.publish_date.split('T')[0] : today, ...m.meta, files: m.attachments || [] }); setModal(true); }}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="edit" size={20} color="white" />
                      </div>
                    </IonItemOption>
                    <IonItemOption color="danger" onClick={() => setDelItem(m)}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="trash" size={20} color="white" />
                      </div>
                    </IonItemOption>
                  </IonItemOptions>
                )}
              </IonItemSliding>
            ))}
          </div>
        </Card>
      )}

      {!loading && currentTabType === "resolution" && (
        <Card>
          <SectionHeader title={t("title_resolutions")} action={!isMember && <div className="hidden lg:block"><Btn sm variant="primary" icon="plus" onClick={() => { setEditItem(null); setForm(def); setModal(true); }}>{t("btn_new_resolution")}</Btn></div>} />
          <div className="hidden lg:block">
            <DataTable cols={[t("col_resolution"), t("col_date"), t("col_approved_by"), t("col_status"), t("col_actions")]}
              rows={items.Resolutions.map(r => [
                <div className="flex flex-col max-w-[300px] md:max-w-[400px] lg:max-w-[600px]">
                  <span className="font-semibold text-sm text-ink truncate">{r.title}</span>
                  <span className="text-sm text-muted truncate block">{r.content}</span>
                </div>,
                r.publish_date ? formatDateByPreference(r.publish_date) : '-',
                r.meta?.approved_by || "-",
                <div className="flex gap-3">
                  <Badge color={r.meta?.status === "Passed" ? "green" : r.meta?.status === "Pending" ? "amber" : "red"} dot>{(r.meta?.status || "Passed") === "Passed" ? t("status_passed") : (r.meta?.status === "Pending" ? t("status_pending") : t("status_rejected"))}</Badge>
                  {r.meta?.draft_status === "draft" && <Badge color="gray" dot>{t("status_draft")}</Badge>}
                </div>,
                <div className="flex gap-2">
                  {r.attachments?.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setViewFiles(r.attachments); }}
                      className="text-sm text-primary font-medium flex items-center gap-1 hover:underline"
                    >
                      <Icon name="link" size={10} /> {r.attachments.length} {t("label_files")}
                    </button>
                  )}
                  {!isMember && (
                    <>
                      {r.meta?.draft_status === "draft" && <Btn xs icon="check" variant="primary" onClick={() => setPubItem(r)}>{t("btn_publish")}</Btn>}
                      <Btn xs icon="edit" onClick={() => { setEditItem(r); setForm({ ...def, ...r, publish_date: r.publish_date ? r.publish_date.split('T')[0] : today, ...r.meta, files: r.attachments || [] }); setModal(true); }}>{t("btn_edit")}</Btn>
                      <Btn xs variant="danger" icon="trash" onClick={() => setDelItem(r)}>{t("btn_del")}</Btn>
                    </>
                  )}
                </div>
              ])} />
          </div>

          {/* Mobile View */}
          <div className="block lg:hidden space-y-3 mt-3">
            {items.Resolutions.length === 0 && <p className="text-sm text-muted text-center py-8">No resolutions found.</p>}
            {items.Resolutions.map((r, i) => (
              <IonItemSliding key={r.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                  <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                    <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name="check" size={16} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-ink truncate mb-1">{r.title}</div>
                      <div className="text-xs text-muted line-clamp-2 leading-snug mb-1">{r.content}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge color={r.meta?.status === "Passed" ? "green" : r.meta?.status === "Pending" ? "amber" : "red"} sm>{(r.meta?.status || "Passed") === "Passed" ? t("status_passed") : (r.meta?.status === "Pending" ? t("status_pending") : t("status_rejected"))}</Badge>
                        {r.meta?.draft_status === "draft" && <Badge color="gray" dot sm>{t("status_draft")}</Badge>}
                        <span className="text-[10px] text-muted">📅 {r.publish_date ? formatDateByPreference(r.publish_date) : '-'}</span>
                      </div>
                    </div>

                    {r.attachments?.length > 0 && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0 items-center justify-center pt-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setViewFiles(r.attachments); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Icon name="link" size={14} />
                        </button>
                      </div>
                    )}

                    {!isMember && (
                      <div className="text-slate-300 ml-1 flex items-center justify-center pt-3">
                        <Icon name="chevron_left" size={14} />
                      </div>
                    )}
                  </div>
                </IonItem>

                {!isMember && (
                  <IonItemOptions side="end">
                    {r.meta?.draft_status === "draft" && (
                      <IonItemOption color="success" onClick={() => setPubItem(r)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="check" size={20} color="white" />
                        </div>
                      </IonItemOption>
                    )}
                    <IonItemOption color="primary" onClick={() => { setEditItem(r); setForm({ ...def, ...r, publish_date: r.publish_date ? r.publish_date.split('T')[0] : today, ...r.meta, files: r.attachments || [] }); setModal(true); }}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="edit" size={20} color="white" />
                      </div>
                    </IonItemOption>
                    <IonItemOption color="danger" onClick={() => setDelItem(r)}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="trash" size={20} color="white" />
                      </div>
                    </IonItemOption>
                  </IonItemOptions>
                )}
              </IonItemSliding>
            ))}
          </div>
        </Card>
      )}

      {!loading && currentTabType === "letter" && (
        <Card>
          <SectionHeader title={t("title_letters")} action={!isMember && <div className="hidden lg:block"><Btn sm variant="primary" icon="plus" onClick={() => { setEditItem(null); setForm(def); setModal(true); }}>{t("btn_new_letter")}</Btn></div>} />
          <div className="hidden lg:block">
            <DataTable cols={[t("col_type"), t("col_subject"), t("col_recipient"), t("col_date"), t("col_actions")]}
              rows={items.Letters.map(l => [
                <Badge color={l.meta?.letter_type === "Outgoing" ? "red" : "blue"}>{(l.meta?.letter_type || "Outgoing") === "Outgoing" ? t("type_outgoing") : t("type_incoming")}</Badge>,
                <div className="flex flex-col max-w-[300px] md:max-w-[400px] lg:max-w-[600px]">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-ink truncate">{l.title}</span>
                    {l.meta?.draft_status === "draft" && <Badge color="gray" dot>{t("status_draft")}</Badge>}
                  </div>
                  <span className="text-sm text-muted truncate block">{l.content}</span>
                </div>,
                l.meta?.recipient || "-",
                l.publish_date ? formatDateByPreference(l.publish_date) : '-',
                <div className="flex gap-3 items-center">
                  {l.attachments?.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setViewFiles(l.attachments); }}
                      className="text-sm text-primary font-medium flex items-center gap-1 hover:underline mt-0.5"
                    >
                      <Icon name="link" size={10} /> {l.attachments.length} {t("label_files")}
                    </button>
                  )}
                  {!isMember && (
                    <div className="flex gap-1 items-center">
                      {l.meta?.draft_status === "draft" && <Btn xs icon="check" variant="primary" onClick={() => setPubItem(l)}>{t("btn_publish")}</Btn>}
                      <Btn xs icon="edit" onClick={() => { setEditItem(l); setForm({ ...def, ...l, publish_date: l.publish_date ? l.publish_date.split('T')[0] : today, ...l.meta, type: l.meta?.letter_type, files: l.attachments || [] }); setModal(true); }}>{t("btn_edit")}</Btn>
                      <Btn xs variant="danger" icon="trash" onClick={() => setDelItem(l)}>{t("btn_del")}</Btn>
                    </div>
                  )}
                </div>
              ])} />
          </div>

          {/* Mobile View */}
          <div className="block lg:hidden space-y-3 mt-3">
            {items.Letters.length === 0 && <p className="text-sm text-muted text-center py-8">No letters found.</p>}
            {items.Letters.map((l, i) => (
              <IonItemSliding key={l.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                  <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 ${l.meta?.letter_type === 'Outgoing' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                      <Icon name={l.meta?.letter_type === 'Outgoing' ? 'chevron_right' : 'chevron_left'} size={16} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-ink truncate mb-1">{l.title}</div>
                      <div className="text-xs text-muted line-clamp-2 leading-snug mb-1">{l.content}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge color={l.meta?.letter_type === "Outgoing" ? "red" : "blue"} sm>{(l.meta?.letter_type || "Outgoing") === "Outgoing" ? t("type_outgoing") : t("type_incoming")}</Badge>
                        {l.meta?.draft_status === "draft" && <Badge color="gray" dot sm>{t("status_draft")}</Badge>}
                        <span className="text-[10px] text-muted">📅 {l.publish_date ? formatDateByPreference(l.publish_date) : '-'}</span>
                        {l.meta?.recipient && <span className="text-[10px] text-muted">👤 {l.meta.recipient}</span>}
                      </div>
                    </div>

                    {l.attachments?.length > 0 && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0 items-center justify-center pt-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setViewFiles(l.attachments); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Icon name="link" size={14} />
                        </button>
                      </div>
                    )}

                    {!isMember && (
                      <div className="text-slate-300 ml-1 flex items-center justify-center pt-3">
                        <Icon name="chevron_left" size={14} />
                      </div>
                    )}
                  </div>
                </IonItem>

                {!isMember && (
                  <IonItemOptions side="end">
                    {l.meta?.draft_status === "draft" && (
                      <IonItemOption color="success" onClick={() => setPubItem(l)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="check" size={20} color="white" />
                        </div>
                      </IonItemOption>
                    )}
                    <IonItemOption color="primary" onClick={() => { setEditItem(l); setForm({ ...def, ...l, publish_date: l.publish_date ? l.publish_date.split('T')[0] : today, ...l.meta, type: l.meta?.letter_type, files: l.attachments || [] }); setModal(true); }}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="edit" size={20} color="white" />
                      </div>
                    </IonItemOption>
                    <IonItemOption color="danger" onClick={() => setDelItem(l)}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="trash" size={20} color="white" />
                      </div>
                    </IonItemOption>
                  </IonItemOptions>
                )}
              </IonItemSliding>
            ))}
          </div>
        </Card>
      )}

      {!loading && currentTabType === "sop" && (
        <Card>
          <SectionHeader title={t("title_sops")} action={!isMember && <div className="hidden lg:block"><Btn sm variant="primary" icon="plus" onClick={() => { setEditItem(null); setForm(def); setModal(true); }}>{t("btn_add_sop")}</Btn></div>} />
          <div className="hidden lg:block">
            <DataTable cols={[t("col_title"), t("col_category"), t("col_date"), t("col_actions")]}
              rows={items.SOPs.map(s => [
                <div className="flex items-center gap-2 max-w-[300px] md:max-w-[400px] lg:max-w-[600px]">
                  <span className="font-semibold text-sm truncate">{s.title}</span>
                  {s.meta?.draft_status === "draft" && <Badge color="gray" dot>{t("status_draft")}</Badge>}
                </div>,
                <Badge color={s.category === "Emergency" ? "red" : s.category === "Security" ? "blue" : "green"}>{t("cat_" + s.category.toLowerCase().replace(" ", "_")) || s.category}</Badge>,
                s.publish_date ? formatDateByPreference(s.publish_date) : '-',
                <div className="flex gap-1">
                  {s.attachments?.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setViewFiles(s.attachments); }}
                      className="text-sm text-primary font-medium flex items-center gap-1 hover:underline mt-0.5"
                    >
                      <Icon name="link" size={10} /> {s.attachments.length} {t("label_files")}
                    </button>
                  )}
                  {!isMember && (
                    <>
                      {s.meta?.draft_status === "draft" && <Btn xs icon="check" variant="primary" onClick={() => setPubItem(s)}>{t("btn_publish")}</Btn>}
                      <Btn xs icon="edit" onClick={() => { setEditItem(s); setForm({ ...def, ...s, publish_date: s.publish_date ? s.publish_date.split('T')[0] : today, ...s.meta, files: s.attachments || [] }); setModal(true); }}>{t("btn_edit")}</Btn>
                      <Btn xs variant="danger" icon="trash" onClick={() => setDelItem(s)}>{t("btn_del")}</Btn>
                    </>
                  )}
                </div>
              ])} />
          </div>

          {/* Mobile View */}
          <div className="block lg:hidden space-y-3 mt-3">
            {items.SOPs.length === 0 && <p className="text-sm text-muted text-center py-8">No SOPs found.</p>}
            {items.SOPs.map((s, i) => (
              <IonItemSliding key={s.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                  <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 ${s.category === "Emergency" ? "bg-red-50 text-red-600" : s.category === "Security" ? "bg-blue-50 text-blue-600" : "bg-green-50 text-green-600"}`}>
                      <Icon name="shield" size={16} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-ink truncate mb-1">{s.title}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge color={s.category === "Emergency" ? "red" : s.category === "Security" ? "blue" : "green"} sm>{t("cat_" + s.category.toLowerCase().replace(" ", "_")) || s.category}</Badge>
                        {s.meta?.draft_status === "draft" && <Badge color="gray" dot sm>{t("status_draft")}</Badge>}
                        <span className="text-[10px] text-muted">📅 {s.publish_date ? formatDateByPreference(s.publish_date) : '-'}</span>
                      </div>
                    </div>

                    {s.attachments?.length > 0 && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0 items-center justify-center pt-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setViewFiles(s.attachments); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Icon name="link" size={14} />
                        </button>
                      </div>
                    )}

                    {!isMember && (
                      <div className="text-slate-300 ml-1 flex items-center justify-center pt-3">
                        <Icon name="chevron_left" size={14} />
                      </div>
                    )}
                  </div>
                </IonItem>

                {!isMember && (
                  <IonItemOptions side="end">
                    {s.meta?.draft_status === "draft" && (
                      <IonItemOption color="success" onClick={() => setPubItem(s)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="check" size={20} color="white" />
                        </div>
                      </IonItemOption>
                    )}
                    <IonItemOption color="primary" onClick={() => { setEditItem(s); setForm({ ...def, ...s, publish_date: s.publish_date ? s.publish_date.split('T')[0] : today, ...s.meta, files: s.attachments || [] }); setModal(true); }}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="edit" size={20} color="white" />
                      </div>
                    </IonItemOption>
                    <IonItemOption color="danger" onClick={() => setDelItem(s)}>
                      <div className="flex flex-col items-center justify-center w-12">
                        <Icon name="trash" size={20} color="white" />
                      </div>
                    </IonItemOption>
                  </IonItemOptions>
                )}
              </IonItemSliding>
            ))}
          </div>
        </Card>
      )}

      {!loading && currentTabType === "penalty" && (
        <div className="space-y-4">
          {!isMember && (
            <Card>
              <SectionHeader title={t("title_templates")} subtitle={t("subtitle_templates")} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                {PENALTY_TEMPLATES.map(tpl => (
                  <div key={tpl.id} className="border border-border rounded-xl p-4 hover:border-primary/30 hover:bg-slate-50/50 hover:dark:bg-slate-700 transition-all">
                    <div className="flex items-start gap-3 mb-3">
                      <span className="text-2xl flex-shrink-0">{tpl.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-ink">{tpl.name}</div>
                        <div className="text-xs text-muted mt-0.5">{tpl.desc}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Btn xs variant="primary" onClick={() => openWithTemplate(tpl)}>{t("btn_use_template")}</Btn>
                      <Btn xs onClick={() => setExpandedTemplate(expandedTemplate === tpl.id ? null : tpl.id)}>
                        {expandedTemplate === tpl.id ? t("btn_hide") : t("btn_preview")}
                      </Btn>
                    </div>
                    {expandedTemplate === tpl.id && (
                      <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700">
                        <div className="text-sm font-semibold text-muted dark:text-white uppercase tracking-wider mb-1">Subject</div>
                        <div className="text-xs font-medium text-ink dark:text-slate-400 mb-2">{tpl.subject}</div>
                        <div className="text-sm font-semibold text-muted dark:text-white uppercase tracking-wider mb-1">Content</div>
                        <pre className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap font-sans leading-relaxed">{tpl.content}</pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card>
            <SectionHeader
              title={t("title_issued_penalties")}
              subtitle={t("msg_notices_issued", { count: items["Penalties & Notice"].length })}
              action={!isMember && (
                <div className="hidden lg:block">
                  {noticeLimit !== -1 && noticesThisMonth >= noticeLimit ? (
                    <Btn sm variant="primary" icon="plus" onClick={() => toast.error(`Monthly limit reached (${noticesThisMonth}/${noticeLimit}). Please upgrade your plan.`)} className="opacity-50 grayscale">{t("btn_issue_notice")}</Btn>
                  ) : (
                    <Btn sm variant="primary" icon="plus" onClick={() => { setEditItem(null); setForm(def); setModal(true); }}>{t("btn_issue_notice")}</Btn>
                  )}
                </div>
              )}
            />
            {items["Penalties & Notice"].length === 0 ? (
              <p className="text-sm text-muted text-center py-8">{t("msg_no_penalties")}</p>
            ) : (
              <>
                <div className="hidden lg:block">
                  <DataTable
                    cols={[t("col_unit"), t("col_subject"), t("col_issued_on"), t("col_status"), t("col_actions")]}
                    rows={items["Penalties & Notice"].map(n => [
                      <span className="font-mono text-xs font-bold bg-slate-50 dark:bg-slate-700 px-1.5 py-0.5 rounded">{n.meta?.unit_no || "-"}</span>,
                      <div className="flex flex-col max-w-[300px] md:max-w-[400px] lg:max-w-[600px]">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-ink truncate">{n.title}</span>
                          {n.meta?.draft_status === "draft" && <Badge color="gray" dot>{t("status_draft")}</Badge>}
                        </div>
                        <span className="text-sm text-muted truncate block dark:text-slate-400">{n.content}</span>
                      </div>,
                      n.publish_date ? formatDateByPreference(n.publish_date) : '-',
                      <Badge color="blue" dot>{n.meta?.draft_status === "draft" ? t("status_draft") : t("status_issued")}</Badge>,
                      <div className="flex flex-col gap-1 items-start">
                        {!isMember && (
                          <div className="flex gap-1 items-center">
                            {n.meta?.draft_status === "draft" && <Btn xs icon="check" variant="primary" onClick={() => setPubItem(n)}>{t("btn_publish")}</Btn>}
                            <Btn xs icon="edit" onClick={() => { setEditItem(n); setForm({ ...def, ...n, publish_date: n.publish_date ? n.publish_date.split('T')[0] : today, ...n.meta, files: n.attachments || [] }); setModal(true); }}>{t("btn_edit")}</Btn>
                            <Btn xs variant="danger" icon="trash" onClick={() => setDelItem(n)}>{t("btn_del")}</Btn>
                          </div>
                        )}
                        {n.attachments?.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setViewFiles(n.attachments); }}
                            className="text-sm text-primary font-medium flex items-center gap-1 hover:underline mt-0.5"
                          >
                            <Icon name="link" size={10} /> {n.attachments.length} {t("label_files")}
                          </button>
                        )}
                      </div>
                    ])}
                  />
                </div>

                {/* Mobile View */}
                <div className="block lg:hidden space-y-3 mt-3">
                  {items["Penalties & Notice"].map((n, i) => (
                    <IonItemSliding key={n.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                      <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                        <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                          <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex flex-col items-center justify-center flex-shrink-0 mt-1 shadow-sm border border-orange-100">
                            <span className="text-[10px] font-bold uppercase text-orange-400 leading-none">UNIT</span>
                            <span className="text-xs font-bold font-mono">{n.meta?.unit_no || "-"}</span>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-ink truncate mb-1">{n.title}</div>
                            <div className="text-xs text-muted line-clamp-2 leading-snug mb-1">{n.content}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <Badge color="blue" sm>{n.meta?.draft_status === "draft" ? t("status_draft") : t("status_issued")}</Badge>
                              <span className="text-[10px] text-muted">📅 {n.publish_date ? formatDateByPreference(n.publish_date) : '-'}</span>
                            </div>
                          </div>

                          {n.attachments?.length > 0 && (
                            <div className="flex flex-col gap-1.5 flex-shrink-0 items-center justify-center pt-2">
                              <button type="button" onClick={(e) => { e.stopPropagation(); setViewFiles(n.attachments); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                                <Icon name="link" size={14} />
                              </button>
                            </div>
                          )}

                          {!isMember && (
                            <div className="text-slate-300 ml-1 flex items-center justify-center pt-3">
                              <Icon name="chevron_left" size={14} />
                            </div>
                          )}
                        </div>
                      </IonItem>

                      {!isMember && (
                        <IonItemOptions side="end">
                          {n.meta?.draft_status === "draft" && (
                            <IonItemOption color="success" onClick={() => setPubItem(n)}>
                              <div className="flex flex-col items-center justify-center w-12">
                                <Icon name="check" size={20} color="white" />
                              </div>
                            </IonItemOption>
                          )}
                          <IonItemOption color="primary" onClick={() => { setEditItem(n); setForm({ ...def, ...n, publish_date: n.publish_date ? n.publish_date.split('T')[0] : today, ...n.meta, files: n.attachments || [] }); setModal(true); }}>
                            <div className="flex flex-col items-center justify-center w-12">
                              <Icon name="edit" size={20} color="white" />
                            </div>
                          </IonItemOption>
                          <IonItemOption color="danger" onClick={() => setDelItem(n)}>
                            <div className="flex flex-col items-center justify-center w-12">
                              <Icon name="trash" size={20} color="white" />
                            </div>
                          </IonItemOption>
                        </IonItemOptions>
                      )}
                    </IonItemSliding>
                  ))}
                </div>
              </>
            )}
          </Card>
        </div>
      )}
      {viewFiles && (
        <Modal title={t("title_view_attachments")} onClose={() => setViewFiles(null)}>
          <div className="space-y-2.5">
            {viewFiles.map((file, i) => (
              <div key={file.id || i} className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-700 border border-slate-100 dark:border-slate-700 rounded-xl hover:border-primary/20 transition-all">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-primary dark:text-white border border-blue-100 dark:border-primary flex items-center justify-center text-primary flex-shrink-0">
                    <Icon name={file.extension === 'pdf' ? 'documents' : 'eye'} size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">{file.original_name}</div>
                    <div className="text-xs text-muted mt-0.5">{(file.size / 1024).toFixed(1)} KB · {file.extension?.toUpperCase() || file.mime_type?.split('/')[1]?.toUpperCase() || "FILE"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleView(file)}
                    className="px-3 py-2 bg-primary border border-slate-200 dark:border-slate-700 hover:bg-primary/90 text-white text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
                  >
                    <Icon name="eye" size={12} />
                    {t("btn_view")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* FAB for Mobile */}
      {!isMember && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => { setEditItem(null); setForm(def); setModal(true); }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
